import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet,
    Chip
} from '@mui/joy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import axiosConfig from '../util/axiosConfig';

const AssessmentStatus = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assessments, setAssessments] = useState([]);

    useEffect(() => {
        fetchAssessmentStatus();
    }, []);

    const fetchAssessmentStatus = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/assessment-status');
            setAssessments(response.data.assessments || []);
        } catch (err) {
            console.error('Error fetching assessment status:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load assessment status data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading assessment status...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (assessments.length === 0) {
        return (
            <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
                <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="h4" sx={{ fontWeight: 'bold', color: '#6e7072' }}>
                        Assessment not yet scheduled
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AssessmentIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                        <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                            Assessment Status
                        </Typography>
                    </Box>
                    <Sheet
                        variant="outlined"
                        sx={{
                            width: '100%',
                            borderRadius: 'sm',
                            overflow: 'auto',
                            maxHeight: '600px',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Table
                            aria-label="assessment status table"
                            sx={{
                                '& tbody tr:nth-child(even)': {
                                    backgroundColor: '#f9f9f9'
                                },
                                '& tbody tr:hover': {
                                    backgroundColor: '#fff5ed'
                                },
                                '& thead': {
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                },
                                '& thead th': {
                                    backgroundColor: '#eb7a24',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10,
                                    borderBottom: '2px solid',
                                    borderBottomColor: '#e55125'
                                },
                                '& td, & th': {
                                    padding: '12px',
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }
                            }}
                        >
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Module</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {assessments.map((assessment, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Typography level="body-md">
                                                {assessment.date || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-md">
                                                {assessment.module || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Chip
                                                color={assessment.statusColor}
                                                size="sm"
                                                variant="soft"
                                            >
                                                {assessment.status || '-'}
                                            </Chip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </CardContent>
            </Card>
        </Box>
    );
};

export default AssessmentStatus;

